import type { AuthChangeEvent, Session, SupabaseClient, User, UserMetadata } from '@supabase/supabase-js';
import { supabaseConfiguration } from '../config/supabase';
import type { InviteCodeHash } from '../utils/inviteCode';

type SupabaseAuthClient = SupabaseClient['auth'];

export interface AuthSignUpResult {
  needsEmailConfirmation: boolean;
  session: Session | null;
  user: User | null;
}

export interface AuthService {
  readonly configurationMessage: string;
  readonly isConfigured: boolean;
  getCurrentUser(): Promise<User>;
  getSession(): Promise<Session | null>;
  onAuthStateChange(callback: (event: AuthChangeEvent, session: Session | null) => void): () => void;
  requestPasswordReset(email: string): Promise<void>;
  signIn(email: string, password: string): Promise<Session>;
  signOut(): Promise<void>;
  signUp(email: string, password: string, inviteCodeHash: InviteCodeHash, displayName: string): Promise<AuthSignUpResult>;
  updatePassword(password: string): Promise<void>;
  updateUserMetadata(patch: UserMetadata): Promise<User>;
}

export type AuthErrorKind =
  | 'email_in_use'
  | 'email_not_confirmed'
  | 'invalid_credentials'
  | 'invite_expired'
  | 'invite_invalid'
  | 'invite_revoked'
  | 'invite_used'
  | 'network'
  | 'not_configured'
  | 'rate_limit'
  | 'unknown'
  | 'weak_password';

export class AuthServiceError extends Error {
  constructor(
    message: string,
    readonly kind: AuthErrorKind,
    readonly cause?: unknown
  ) {
    super(message);
    this.name = 'AuthServiceError';
  }
}

interface ErrorLike {
  code?: string;
  message?: string;
  status?: number;
}

const isErrorLike = (error: unknown): error is ErrorLike => typeof error === 'object' && error !== null;

const normalizeAuthError = (error: unknown): AuthServiceError => {
  if (error instanceof AuthServiceError) return error;

  const details = isErrorLike(error) ? error : undefined;
  const code = details?.code?.toLowerCase();
  const message = details?.message?.toLowerCase() ?? '';

  if (message.includes('leitour_invite_used')) {
    return new AuthServiceError('Este código já foi utilizado.', 'invite_used', error);
  }
  if (message.includes('leitour_invite_expired')) {
    return new AuthServiceError('Este código expirou.', 'invite_expired', error);
  }
  if (message.includes('leitour_invite_revoked')) {
    return new AuthServiceError('Este convite foi revogado.', 'invite_revoked', error);
  }
  if (
    message.includes('leitour_invite_missing')
    || message.includes('leitour_invite_invalid')
    || message.includes('leitour_invite_reserved')
    || message.includes('leitour_invite_finalization_failed')
  ) {
    return new AuthServiceError('Código de convite inválido.', 'invite_invalid', error);
  }
  if (code === 'invalid_credentials') {
    return new AuthServiceError('E-mail ou senha incorretos.', 'invalid_credentials', error);
  }
  if (code === 'email_not_confirmed') {
    return new AuthServiceError('Confirme seu e-mail antes de entrar.', 'email_not_confirmed', error);
  }
  if (code === 'user_already_exists' || code === 'email_exists' || message.includes('already registered')) {
    return new AuthServiceError('Já existe uma conta com este e-mail.', 'email_in_use', error);
  }
  if (code === 'weak_password' || message.includes('password should be')) {
    return new AuthServiceError('Use uma senha mais forte, com pelo menos 8 caracteres.', 'weak_password', error);
  }
  if (details?.status === 429 || code?.includes('rate_limit')) {
    return new AuthServiceError('Muitas tentativas em pouco tempo. Aguarde alguns minutos e tente novamente.', 'rate_limit', error);
  }
  if (error instanceof TypeError || message.includes('fetch') || message.includes('network')) {
    return new AuthServiceError('Sem conexão com o serviço de conta. Verifique sua internet e tente novamente.', 'network', error);
  }

  return new AuthServiceError('Não foi possível concluir esta ação. Tente novamente.', 'unknown', error);
};

const getAuthRedirectUrl = () => {
  if (typeof window === 'undefined') return undefined;
  return `${window.location.origin}${window.location.pathname}`;
};

export const createAuthService = (
  client: SupabaseAuthClient | null,
  configurationMessage = client ? 'Supabase configurado.' : 'Configuração do Supabase pendente neste ambiente.'
): AuthService => {
  const requireClient = () => {
    if (!client) throw new AuthServiceError(configurationMessage, 'not_configured');
    return client;
  };

  const safely = async <Result,>(operation: () => Promise<Result>): Promise<Result> => {
    try {
      return await operation();
    } catch (error) {
      throw normalizeAuthError(error);
    }
  };

  return {
    configurationMessage,
    isConfigured: client !== null,

    async signUp(email, password, inviteCodeHash, displayName) {
      return safely(async () => {
        const auth = requireClient();
        const emailRedirectTo = getAuthRedirectUrl();
        const { data, error } = await auth.signUp({
          email: email.trim(),
          password,
          options: {
            data: { invite_code_hash: inviteCodeHash, display_name: displayName.trim() },
            ...(emailRedirectTo ? { emailRedirectTo } : {})
          }
        });
        if (error) throw error;
        return {
          needsEmailConfirmation: Boolean(data.user && !data.session),
          session: data.session,
          user: data.user
        };
      });
    },

    async signIn(email, password) {
      return safely(async () => {
        const auth = requireClient();
        const { data, error } = await auth.signInWithPassword({ email: email.trim(), password });
        if (error) throw error;
        if (!data.session) throw new AuthServiceError('Não foi possível iniciar a sessão.', 'unknown');
        return data.session;
      });
    },

    async signOut() {
      await safely(async () => {
        const { error } = await requireClient().signOut();
        if (error) throw error;
      });
    },

    async requestPasswordReset(email) {
      await safely(async () => {
        const redirectTo = getAuthRedirectUrl();
        const { error } = await requireClient().resetPasswordForEmail(
          email.trim(),
          redirectTo ? { redirectTo } : undefined
        );
        if (error) throw error;
      });
    },

    async updatePassword(password) {
      await safely(async () => {
        const { error } = await requireClient().updateUser({ password });
        if (error) throw error;
      });
    },

    async updateUserMetadata(patch) {
      return safely(async () => {
        const { data, error } = await requireClient().updateUser({ data: patch });
        if (error) throw error;
        if (!data.user) throw new AuthServiceError('Não foi possível atualizar o perfil.', 'unknown');
        return data.user;
      });
    },

    async getCurrentUser() {
      return safely(async () => {
        const { data, error } = await requireClient().getUser();
        if (error) throw error;
        if (!data.user) throw new AuthServiceError('Sessão autenticada necessária.', 'unknown');
        return data.user;
      });
    },

    async getSession() {
      return safely(async () => {
        const { data, error } = await requireClient().getSession();
        if (error) throw error;
        return data.session;
      });
    },

    onAuthStateChange(callback) {
      const { data } = requireClient().onAuthStateChange(callback);
      return () => data.subscription.unsubscribe();
    }
  };
};

export const authService = createAuthService(
  supabaseConfiguration.client?.auth ?? null,
  supabaseConfiguration.message
);
